import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Modal Priority Service
 * Coordinates between different modals to ensure proper priority handling.
 * Priority order: account-disabled > session-revoked > token-expiration
 */
@Injectable({
  providedIn: 'root'
})
export class ModalPriorityService {
  private prioritySubject = new BehaviorSubject<'account-disabled' | 'session-revoked' | 'token-expiration' | null>(null);
  public priority$: Observable<'account-disabled' | 'session-revoked' | 'token-expiration' | null> = 
    this.prioritySubject.asObservable();

  /**
   * Show account disabled modal (highest priority)
   * This will close any lower priority modals
   */
  showAccountDisabled(): void {
    this.prioritySubject.next('account-disabled');
  }

  /**
   * Show session revoked modal (medium priority)
   * Only shows if account disabled is not already showing
   */
  showSessionRevoked(): void {
    // Only show if account disabled is not already showing
    if (this.prioritySubject.value !== 'account-disabled') {
      this.prioritySubject.next('session-revoked');
    }
  }

  /**
   * Show token expiration modal (lowest priority)
   * Only shows if no higher priority modals are showing
   */
  showTokenExpiration(): void {
    // Only show if no higher priority modals are showing
    if (!this.prioritySubject.value) {
      this.prioritySubject.next('token-expiration');
    }
  }

  /**
   * Clear all modals
   */
  clear(): void {
    this.prioritySubject.next(null);
  }

  /**
   * Get current priority (for checking state)
   */
  getCurrentPriority(): 'account-disabled' | 'session-revoked' | 'token-expiration' | null {
    return this.prioritySubject.value;
  }
}
